import { Link } from 'react-router-dom';
import { IconHeart, IconStar } from './Icons';
import type { PropertySummary } from '../api/types';
import { availabilityLabel, distanceLabel, humanise, priceRange, sharingLabel } from '../utils/format';

interface Props {
  property: PropertySummary;
  isSaved?: boolean;
  onToggleSave?: (propertyId: string) => void;
  showDistance?: boolean;
}

export function PropertyCard({ property, isSaved, onToggleSave, showDistance = true }: Props) {
  const availability = availabilityLabel(
    property.availabilityConfidence,
    property.availableUnits,
    property.category === 'Hotel' ? 'rooms' : 'beds',
  );

  const distance = showDistance ? distanceLabel(property.distanceKm) : null;
  const href = `/listing/${property.slug}`;

  return (
    <article className="sf-listing-card">
      <div className="sf-listing-card__media">
        {property.primaryThumbnailUrl ? (
          <img
            src={property.primaryThumbnailUrl}
            alt={`${property.title}, ${property.locality}`}
            loading="lazy"
            width={400}
            height={300}
          />
        ) : (
          <div className="sf-listing-card__placeholder">No photo yet</div>
        )}

        {property.isFeatured && <span className="sf-featured-flag">Featured</span>}
        {property.photoCount > 1 && (
          <span className="badge bg-dark position-absolute top-0 start-0 m-2">
            {property.photoCount} photos
          </span>
        )}

        {onToggleSave && (
          <button
            type="button"
            className="sf-save-button"
            aria-pressed={isSaved ?? false}
            aria-label={isSaved ? `Remove ${property.title} from favourites` : `Save ${property.title} to favourites`}
            onClick={() => onToggleSave(property.id)}
          >
            <IconHeart fill={isSaved ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      <div className="sf-listing-card__body">
        <h3 className="sf-listing-card__title">
          <Link to={href}>{property.title}</Link>
          {/*
            A chain lists each branch separately, and three branches of one brand carry
            near-identical titles. The branch name is what tells them apart, so it is
            shown beside the title rather than buried in the detail page.
          */}
          {property.branchName && (
            <span className="sf-listing-card__branch"> · {property.branchName}</span>
          )}
        </h3>

        <p className="sf-listing-card__meta mb-0">
          {property.locality}, {property.city}
          {property.businessName && <> · {property.businessName}</>}
          {distance && <> · {distance}</>}
        </p>

        <CategoryFacts property={property} />

        <div className="sf-listing-card__price mt-auto">
          {priceRange(property.priceFrom, property.priceTo, property.priceUnit)}
        </div>

        {/* PG and hotels advertise vacancy; a home is a single unit, so a bed count is
            meaningless for it. */}
        {property.category !== 'HomeRental' && (
          <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>
        )}
        <Link to={href} className="btn btn-sm btn-outline-primary align-self-start mt-2">
          See facilities &amp; details
        </Link>
      </div>
    </article>
  );
}

function CategoryFacts({ property }: { property: PropertySummary }) {
  if (property.category === 'PgHostel') {
    const sharing = property.sharingTypes.slice(0, 2).map(sharingLabel).join(', ');

    return (
      <p className="sf-listing-card__meta mb-0">
        {property.genderPolicy && <>{humanise(property.genderPolicy)}</>}
        {sharing && <> · {sharing}</>}
        {property.foodProvided && <> · Food included</>}
      </p>
    );
  }

  if (property.category === 'HomeRental') {
    return (
      <p className="sf-listing-card__meta mb-0">
        {property.bedrooms !== undefined && <>{property.bedrooms} BHK</>}
        {property.homeType && <> · {humanise(property.homeType)}</>}
        {property.furnishing && <> · {humanise(property.furnishing)}</>}
      </p>
    );
  }

  return (
    <p className="sf-listing-card__meta mb-0">
      {property.hotelType && <>{humanise(property.hotelType)}</>}
      <StarRating rating={property.starRating} source={property.starRatingSource} />
    </p>
  );
}

/**
 * A star rating is only ever shown with its provenance. An owner's own claim is labelled
 * as such, so it is never mistaken for a verified classification.
 */
export function StarRating({ rating, source }: { rating?: number; source?: string }) {
  if (!rating || source === 'None' || !source) return null;

  const verified = source === 'PlatformVerified';

  return (
    <>
      {' · '}
      <span title={verified ? 'Verified by RoomKart' : 'Stated by the property, not verified'}>
        {Array.from({ length: rating }, (_, i) => (
              <IconStar key={i} fill="currentColor" />
            ))}
        <span className="sf-muted"> {verified ? '(verified)' : '(owner stated)'}</span>
      </span>
    </>
  );
}
